import { useState, useEffect } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { StatusBadge } from "./StatusBadge";
import { fcfa, shortDate } from "../lib/format";
import { fileUrl } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle } from "lucide-react";

const STATUS_FLOW = ["OPEN", "IN_PROGRESS", "COMPLETED", "CANCELLED"];

export function RequestDialog({ request, open, onClose, isAdmin, onUpdated }) {
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [estCost, setEstCost] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (request) {
      setStatus(request.status);
      setPriority(request.priority);
      setEstCost(request.estimated_cost || "");
    }
  }, [request]);

  if (!request) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/maintenance-requests/${request.id}`, {
        status, priority, estimated_cost: estCost ? Number(estCost) : null,
      });
      toast.success("Request updated");
      onUpdated?.();
      onClose();
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="request-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-3">
            {request.category} <StatusBadge status={request.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <p className="text-sm text-muted-foreground">{request.description}</p>

          {request.photos?.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {request.photos.map((p, i) => (
                <img key={i} src={fileUrl(p)} alt="issue" className="h-24 w-full object-cover rounded-lg border border-border" data-testid={`request-photo-${i}`} />
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-muted-foreground">Priority</span><p className="font-medium">{request.priority}</p></div>
            <div><span className="text-muted-foreground">Submitted</span><p className="font-medium">{shortDate(request.created_at)}</p></div>
            <div><span className="text-muted-foreground">Assigned to</span><p className="font-medium">{request.assigned_to || "—"}</p></div>
            <div><span className="text-muted-foreground">Est. cost</span><p className="font-medium">{fcfa(request.estimated_cost)}</p></div>
          </div>

          {/* Timeline */}
          <div>
            <p className="overline text-muted-foreground mb-3">Status timeline</p>
            <div className="space-y-3">
              {(request.timeline || []).map((t, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5" aria-hidden="true" />
                  <div className="text-sm">
                    <p className="font-medium">{t.status.replace(/_/g, " ")}</p>
                    <p className="text-muted-foreground text-xs">{t.note} · {shortDate(t.at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {isAdmin && (
            <div className="border-t border-border pt-4 space-y-4">
              <p className="overline text-accent">Manage request</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="dialog-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_FLOW.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Priority</Label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger data-testid="dialog-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["LOW", "NORMAL", "HIGH", "URGENT"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="est">Estimated cost (FCFA)</Label>
                <Input id="est" type="number" value={estCost} onChange={(e) => setEstCost(e.target.value)} data-testid="dialog-cost-input" />
              </div>
              <Button onClick={save} disabled={saving} className="w-full rounded-full hover:bg-primary/90" data-testid="dialog-save-button">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
